import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const PageContainer = styled.div`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.div`
  position: relative;
  height: 70vh;
  min-height: 500px;
  background-image: url('https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=1600&h=800&fit=crop');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.85) 0%, rgba(185, 28, 28, 0.75) 100%);
  }
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;
    background-attachment: scroll;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  max-width: 800px;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HeroButton = styled.button`
  background: white;
  color: #dc2626;
  border: none;
  padding: 12px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    animation: ${pulse} 0.5s ease;
  }
`;

// Content Container
const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

// Section Styles
const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: backwards;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #991b1b;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #dc2626, #991b1b);
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

// Grid Layouts
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Card Components
const InfoCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideInLeft} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 35px rgba(220, 38, 38, 0.15);
  }
`;

const InfoCardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #991b1b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoCardText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  color: #475569;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '❤️';
    position: absolute;
    left: 0;
    color: #dc2626;
    font-size: 0.9rem;
  }
`;

// Warning Box
const WarningBox = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-left: 4px solid #f59e0b;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const WarningTitle = styled.h4`
  color: #92400e;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const SuccessBox = styled.div`
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  border-left: 4px solid #22c55e;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const RiskBadge = styled.span`
  background: ${props => props.color || '#dc2626'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  margin-right: 0.5rem;
`;

// Doctor Section
const DoctorSection = styled.div`
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  border-radius: 30px;
  padding: 2rem;
  color: white;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  
  @media (min-width: 768px) {
    grid-template-columns: 280px 1fr;
  }
`;

const DoctorImage = styled.img`
  width: 100%;
  max-width: 250px;
  border-radius: 20px;
  box-shadow: 0 20px 35px rgba(0,0,0,0.3);
  margin: 0 auto;
  display: block;
  
  @media (min-width: 768px) {
    max-width: 100%;
  }
`;

const DoctorContent = styled.div``;

const DoctorName = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #fca5a5;
`;

const DoctorTitle = styled.p`
  font-size: 1rem;
  color: #fecaca;
  margin-bottom: 1rem;
`;

const DoctorDescription = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: #fee2e2;
`;

const CTAButton = styled.button`
  background: white;
  color: #991b1b;
  border: none;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
`;

// Image Gallery
const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 15px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (min-width: 768px) {
    height: 200px;
  }
`;

// Stats Component
const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #fff5f5, #fff);
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #dc2626;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

// Main Component
const HeartDiseasePage = () => {
  const handleAppointment = () => {
    alert('Book an appointment with Dr. Raj Kamal for Heart Care');
  };

  const galleryImages = [
    'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=400&h=300&fit=crop',
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Heart Disease & Diabetes</HeroTitle>
          <HeroSubtitle>
            Understanding the deadly connection | Comprehensive cardiac care by Dr. Raj Kamal
          </HeroSubtitle>
                    <HeroButton as={Link} to="/book-appointment">Book Consultation</HeroButton>

        </HeroContent>
      </HeroSection>

      <ContentContainer>
        {/* Statistics Section */}
        <Section delay="0.05s">
          <ThreeColumnGrid>
            <StatCard>
              <StatNumber>2-4x</StatNumber>
              <StatLabel>Higher risk of heart disease if you have diabetes</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>68%</StatNumber>
              <StatLabel>of diabetics die from heart disease or stroke</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>30 min</StatNumber>
              <StatLabel>Daily exercise can reduce heart disease risk by 30-40%</StatLabel>
            </StatCard>
          </ThreeColumnGrid>
        </Section>

        {/* Heart Importance Section */}
        <Section delay="0.1s">
          <SectionTitle>Your Heart: The Engine of Life</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>
                <span>❤️</span> Why Your Heart Matters
              </InfoCardTitle>
              <InfoCardText>
                Your heart is a powerful muscular organ about the size of your fist. It beats approximately 100,000 times per day, pumping about 2,000 gallons of blood through 60,000 miles of blood vessels.
              </InfoCardText>
              <InfoCardText>
                <strong>Key functions of your heart:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Pumps oxygen-rich blood to all organs and tissues</ListItem>
                <ListItem>Removes carbon dioxide and waste products</ListItem>
                <ListItem>Delivers hormones and nutrients throughout the body</ListItem>
                <ListItem>Helps maintain body temperature and pH balance</ListItem>
                <ListItem>Supports immune function through blood circulation</ListItem>
              </ul>
            </InfoCard>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1559757175-7b8c9a0c8e1a?w=600&h=400&fit=crop"
                alt="Heart anatomy"
                style={{ width: '100%', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
              />
              <WarningBox>
                <WarningTitle>⚠️ Heart Attack Warning Signs</WarningTitle>
                <p style={{ color: '#92400e', margin: 0 }}>
                  Chest discomfort, shortness of breath, pain in arms/back/neck/jaw, nausea, lightheadedness, or cold sweats. Women may experience more subtle symptoms.
                </p>
              </WarningBox>
            </div>
          </TwoColumnGrid>
        </Section>

        {/* Diabetes and Heart Disease - Main Focus */}
        <Section delay="0.2s">
          <SectionTitle>The Diabetes-Heart Connection</SectionTitle>
          <SectionSubtitle>
            How diabetes damages your heart and increases cardiovascular risk
          </SectionSubtitle>
          
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>🩸 How Diabetes Harms Your Heart</InfoCardTitle>
              <InfoCardText>
                Diabetes and heart disease share a dangerous relationship. High blood sugar damages blood vessels and nerves that control your heart over time.
              </InfoCardText>
              <InfoCardText>
                <strong>Direct mechanisms of damage:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem><strong>Atherosclerosis:</strong> High blood sugar accelerates plaque buildup in arteries</ListItem>
                <ListItem><strong>Endothelial dysfunction:</strong> Damages the inner lining of blood vessels</ListItem>
                <ListItem><strong>Increased inflammation:</strong> Chronic inflammation promotes artery damage</ListItem>
                <ListItem><strong>Oxidative stress:</strong> High glucose creates harmful free radicals</ListItem>
                <ListItem><strong>Autonomic neuropathy:</strong> Damages nerves that regulate heart rate</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📊 The Silent Threat</InfoCardTitle>
              <InfoCardText>
                <strong>Why diabetes makes heart disease more dangerous:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Diabetics often have "silent" heart attacks without chest pain</ListItem>
                <ListItem>Heart attacks are more severe and have worse outcomes</ListItem>
                <ListItem>Higher risk of heart failure even without blocked arteries</ListItem>
                <ListItem>Recovery after heart attack is slower and complications more common</ListItem>
                <ListItem>Combined with high blood pressure, risk multiplies significantly</ListItem>
              </ul>
              <SuccessBox>
                <WarningTitle>✅ Good News</WarningTitle>
                <p style={{ color: '#166534', margin: 0 }}>
                  Strict blood sugar control can reduce heart disease risk by up to 42% in type 1 diabetics and 25% in type 2 diabetics.
                </p>
              </SuccessBox>
            </InfoCard>
          </TwoColumnGrid>

          <InfoCard style={{ marginTop: '1.5rem' }}>
            <InfoCardTitle>🔄 The Vicious Cycle</InfoCardTitle>
            <InfoCardText>
              Diabetes and heart disease create a dangerous feedback loop. Heart disease makes diabetes harder to manage, and poorly controlled diabetes accelerates heart disease. <strong>This is why integrated care is essential.</strong>
            </InfoCardText>
            <div style={{ 
              background: '#fef2f2', 
              padding: '1rem', 
              borderRadius: '15px', 
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#991b1b' }}>
                High Blood Sugar → Damaged Blood Vessels → Heart Disease → Reduced Blood Flow → Worsened Diabetes Control → Further Heart Damage
              </p>
            </div>
          </InfoCard>
        </Section>

        {/* Types of Heart Disease */}
        <Section delay="0.3s">
          <SectionTitle>Types of Heart Disease</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>🫀 Coronary Artery Disease (CAD)</InfoCardTitle>
              <InfoCardText>
                The most common type, caused by plaque buildup in arteries that supply blood to the heart. This leads to angina (chest pain) and heart attacks.
              </InfoCardText>
              <InfoCardText>
                <strong>In diabetics:</strong> CAD develops earlier, progresses faster, and often affects multiple arteries.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>💓 Heart Failure</InfoCardTitle>
              <InfoCardText>
                When the heart can't pump enough blood to meet the body's needs. Diabetes doubles the risk of heart failure, even without a prior heart attack.
              </InfoCardText>
              <InfoCardText>
                <strong>Symptoms include:</strong> Shortness of breath, fatigue, swelling in legs, rapid heartbeat.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>⚡ Arrhythmias & Stroke</InfoCardTitle>
              <InfoCardText>
                Diabetes increases risk of atrial fibrillation (irregular heartbeat) and stroke by 2-4 times. High blood sugar damages small blood vessels in the brain.
              </InfoCardText>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Prevention and Management */}
        <Section delay="0.4s">
          <SectionTitle>Protecting Your Heart With Diabetes</SectionTitle>
          <SectionSubtitle>
            Evidence-based strategies to reduce cardiovascular risk
          </SectionSubtitle>
          
          <InfoCard>
            <InfoCardTitle>🎯 ABCDE of Heart Health for Diabetics</InfoCardTitle>
            <ThreeColumnGrid>
              <div>
                <RiskBadge color="#dc2626">A</RiskBadge>
                <strong style={{ color: '#1e293b' }}>A1C Control</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Target A1C &lt; 7% for most adults</ListItem>
                  <ListItem>Monitor blood sugar daily</ListItem>
                  <ListItem>Take diabetes medications as prescribed</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#dc2626">B</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Blood Pressure</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Target &lt; 130/80 mmHg</ListItem>
                  <ListItem>Limit sodium to &lt; 1500mg/day</ListItem>
                  <ListItem>Take BP medications consistently</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#dc2626">C</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Cholesterol</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>LDL &lt; 70 mg/dL for high-risk patients</ListItem>
                  <ListItem>Consider statin therapy</ListItem>
                  <ListItem>Increase healthy fats (omega-3s)</ListItem>
                </ul>
              </div>
            </ThreeColumnGrid>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>
              <div>
                <RiskBadge color="#dc2626">D</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Diet & Exercise</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Mediterranean or DASH diet</ListItem>
                  <ListItem>150 min moderate exercise/week</ListItem>
                  <ListItem>Maintain healthy weight (BMI 18.5-24.9)</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#dc2626">E</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Education & Medications</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Take aspirin if prescribed</ListItem>
                  <ListItem>SGLT2 inhibitors or GLP-1 agonists for heart protection</ListItem>
                  <ListItem>Stop smoking immediately</ListItem>
                </ul>
              </div>
            </div>
          </InfoCard>

          <WarningBox style={{ marginTop: '1.5rem' }}>
            <WarningTitle>🚨 When to Seek Emergency Care</WarningTitle>
            <p style={{ color: '#92400e', margin: 0 }}>
              Call emergency services immediately if you experience: Chest pain spreading to arm/jaw/back, sudden severe shortness of breath, fainting, or extreme fatigue. <strong>Don't delay - even if symptoms seem mild!</strong>
            </p>
          </WarningBox>
        </Section>

        {/* Dr. Raj Kamal Section */}
{/*         <Section delay="0.5s">
          <DoctorSection>
            <DoctorGrid>
              <DoctorImage 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop"
                alt="Dr. Raj Kamal"
              />
              <DoctorContent>
                <DoctorName>Dr. Raj Kamal, MD</DoctorName>
                <DoctorTitle>Senior Cardiologist & Diabetes-Heart Specialist</DoctorTitle>
                <DoctorDescription>
                  With over 18 years of specialized experience in managing the intersection of diabetes and cardiovascular disease, Dr. Raj Kamal has helped thousands of patients reduce their heart attack risk and live longer, healthier lives.
                </DoctorDescription>
                <DoctorDescription>
                  <strong>How Dr. Raj Kamal can help you protect your heart:</strong>
                </DoctorDescription>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: '1rem' }}>
                  <ListItem style={{ color: '#fee2e2' }}>Comprehensive cardiovascular risk assessment for diabetics</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>Advanced cardiac testing (Echo, Stress Test, Calcium Score, CT Angiography)</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>Personalized medication plans including heart-protective diabetes drugs</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>Lifestyle modification programs tailored for diabetics</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>Management of hypertension, cholesterol, and obesity</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>Cardiac rehabilitation and post-heart attack care</ListItem>
                  <ListItem style={{ color: '#fee2e2' }}>24/7 emergency support and telemedicine consultations</ListItem>
                </ul>
                <CTAButton onClick={handleAppointment}>Schedule Heart Health Assessment →</CTAButton>
              </DoctorContent>
            </DoctorGrid>
          </DoctorSection>
        </Section> */}

        {/* Heart-Healthy Lifestyle Tips */}
        <Section delay="0.6s">
          <SectionTitle>Heart-Healthy Living With Diabetes</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>🥗 Nutrition Tips</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Eat more fiber-rich foods (oats, beans, lentils)</ListItem>
                <ListItem>Choose whole grains over refined carbs</ListItem>
                <ListItem>Include fatty fish (salmon, mackerel) 2x/week</ListItem>
                <ListItem>Limit saturated fats and trans fats</ListItem>
                <ListItem>Eat nuts, seeds, and avocados for healthy fats</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🏃 Exercise Guidelines</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>150 minutes of moderate aerobic exercise weekly</ListItem>
                <ListItem>Add strength training 2-3 times per week</ListItem>
                <ListItem>Break up sitting time every 30 minutes</ListItem>
                <ListItem>Check blood sugar before and after exercise</ListItem>
                <ListItem>Start slowly and gradually increase intensity</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>😌 Stress Management</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Practice deep breathing or meditation</ListItem>
                <ListItem>Get 7-8 hours of quality sleep</ListItem>
                <ListItem>Stay connected with family and friends</ListItem>
                <ListItem>Join a diabetes support group</ListItem>
                <ListItem>Consider cognitive behavioral therapy</ListItem>
              </ul>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Image Gallery */}
   {/*      <Section delay="0.7s">
          <SectionTitle>Cardiac Care Facilities</SectionTitle>
          <ImageGallery>
            {galleryImages.map((img, index) => (
              <GalleryImage key={index} src={img} alt={`Cardiac facility ${index + 1}`} />
            ))}
          </ImageGallery>
        </Section> */}

        {/* Final Call to Action */}
        <Section delay="0.8s">
          <div style={{ 
            background: 'linear-gradient(135deg, #dc2626, #991b1b)', 
            borderRadius: '20px', 
            padding: '2rem', 
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Don't Wait for Symptoms - Protect Your Heart Today</h3>
            <p style={{ marginBottom: '1.5rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
              If you have diabetes, your heart needs special attention. Early intervention can prevent up to 80% of heart attacks and strokes. Take the first step toward a healthier heart.
            </p>
            <HeroButton onClick={handleAppointment} style={{ background: 'white', color: '#dc2626' }}>
              Book Your Heart Health Assessment
            </HeroButton>
          </div>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default HeartDiseasePage;